/* eslint-disable */
import {
  useEffect,
  useLayoutEffect,
  useState,
  useContext,
  useMemo,
} from "react";
import {
  convertUTCStringToLocalTime,
  convertLocalTimeToUTCString,
  currentLocalDateTime,
  getLocalISOStringDate,
  isFutureDateTime,
} from "../utils/dateUtils";
import { arrayMove } from "@dnd-kit/sortable";
import {
  Badge,
  Button,
  InfoModal,
  Input,
  LoadingShimmer,
  LoadingSpinner,
  Toast,
  useModal,
  useWindowSize,
} from "@newsela/angelou";
import {
  withTrackEvent,
  objectTypes,
  actionNames,
  actionPrefixes,
} from "@newsela/snitch";
import { useRouter } from "next/router";
import PropTypes from "prop-types";

import SortableDeckOfCards from "@/assignments/AssignmentEditor/SortableDeckOfCards";
import useExpirationWarning from "@/assignments/utils/useExpirationWarning";
import globalToasterRef from "@/base/globalToasterRef";
import ClassroomInvitationModal from "@/classrooms/ClassroomInvitationModal";
import CreateNewClassroomModal from "@/classrooms/CreateNewClassroomModal/CreateNewClassroomModal";
import { isClassroomArchived } from "@/components/AssignModal/AssignModal.utils";
import RichTextEditor from "@/components/RichTextEditor";
import { ReadAloudContextProvider } from "@/view/ReadAloudControls/useReadAloudMode";
import { useContent } from "@/content/useContent/useContent";
import { API_BASE_URL } from "@/api/api.config";
import { MONOLITH_ORIGIN } from "@/monolithIntegration/monolithIntegration.constants";
import fetchJson from "@/remoteState/fetchJson";
import capitalize from "@/utils/capitalize";
import useUndo from "@/utils/useUndo";
import contentTypes from "@/view/constants/contentTypes";

import Banner from "../../base/Banner";
import useClassroomGetList from "../../classrooms/useClassroomGetList";
import useUser, { UserCEContext } from "../../users/useUser";
import ListOfSteps from "../ListOfSteps";
import StepItem from "../ListOfSteps/StepItem";
import useAssignmentCreate from "../useAssignmentCreate";
import useAssignmentDelete from "../useAssignmentDelete";
import {
  getExpirationDate,
  getLexileLevelArray,
  gradeLevelFormatter,
  isAllowedUrl,
  urlFormatter,
  flattenAssignmentMembers,
  getHasSRCContent,
  getConsentedSRCContent,
  getIsContentStudentRestricted,
  getLiteContentWithEarliestExpireDate,
  getTruncatedSmallGroupUserNames,
  assigneeSelectionSteps,
} from "../utils/assignmentUtils";
import AssigneeSelector from "./AssigneeSelector/AssigneeSelector";
import AssigneeSelectorModal from "./AssigneeSelectorModal";
import {
  assignmentEditorLabelCss,
  assignmentEditorLayoutCss,
  assignmentSectionCss,
  assignmentSubTitleCss,
  assignmentTitleCss,
  assignmentTitleInputCSS,
  baseDetailsRowCss,
  buttonRowCSS,
  cardCss,
  discardChangesModalCss,
  gridLayoutCss,
  inputsRowCss,
  levelLockingLoadingWrapperCss,
  levelLockHelperTextCSS,
  levelLockMenuCSS,
  modalContentWrapperCss,
  navButtonRowCSS,
  navigationCSS,
  pageTitleCSS,
  stepLayoutCss,
  toastMessageCta,
  toastMessageSize,
  toastMessageSpacing,
  disclaimerCss,
  assignmentContainerCss,
  assignmentStudentInstructionsCss,
  contentLabelCss,
} from "./AssignmentEditor.style";
import AdvisoryModal from "./AdvisoryModal";
import ScheduledDateTimeInput from "./ScheduledDateTimeInput";
import LevelLockMenu from "./LevelLockMenu";
import getUserProperties from "@/users/getUserProperties";

const {
  ASSIGNEE_TYPE_SELECTION_STEP,
  CLASSROOMS_SELECTION_STEP,
  STUDENTS_SELECTION_STEP,
} = assigneeSelectionSteps;

const ButtonWithTrackEvent = withTrackEvent(Button);

const propTypes = {
  /**
   * Pass an assignment object returned from the assignment api
   */
  assignment: PropTypes.object,
  /**
   * Pass an array id strings found in the createdFrom's members.
   */
  contentIds: PropTypes.array,

  /**
   * If copiedAssignmentId is in the url params, pass that value of the original assignment when creating a new assignment from an
   * existing assignment.
   */
  copiedAssignmentId: PropTypes.string,
  /**
   * Pass a string id representing createdFrom contains the contentId of content that we created an assignment from.
   */
  createdFrom: PropTypes.string,
  externalAssignmentContext: PropTypes.shape({
    // externalAssignmentContext is used for integrating LMS assignments.
    action: PropTypes.string, // Option to sync classroom or create assignment.
    courseId: PropTypes.string, // Corresponds to a Newsela classroom ID.
    courseTitle: PropTypes.string, // The name of the external provider classroom. May not match Newsela.
    provider: PropTypes.string, // The name of the external provider.
  }),
};

const MAX_LIMIT_ASSIGNMENT_MEMBERS = 20;

export const MAX_STUDENT_ASSIGNEES_TO_LIST = 7;

const AssignmentEditor = ({
  assignment,
  contentIds,
  copiedAssignmentId,
  createdFrom,
  externalAssignmentContext,
}) => {
  const { data: user } = useUser();
  const userProperties = getUserProperties(user);
  const canCreateClassrooms = userProperties.hasFeature(
    "can_create_classrooms",
  );
  const [currentSelectionOfClassrooms, setCurrentSelectionOfClassrooms] =
    useState(
      assignment?.assignment_classrooms?.map(({ classroom }) =>
        isClassroomArchived(classroom) ? [] : classroom,
      ) || [],
    );
  const [
    currentSelectionOfStudentAssignees,
    setCurrentSelectionOfStudentAssignees,
  ] = useState(assignment?.assignment_groups?.["0"]?.users || []);

  const [validationErrors, setValidationErrors] = useState({});
  const [title, setTitle] = useState("");
  const [titleTouched, setTitleTouched] = useState(false);
  const [assignmentMembers, setAssignmentMembers] = useState([]);
  const [isDraft, setIsDraft] = useState(true);
  const [gradeLevel, setGradeLevel] = useState(null);
  const [dueDate, setDueDate] = useState("");
  const [deleteAssignmentMsg, setDeleteAssignmentMsg] = useState("");
  const [newClassroom, setNewClassroom] = useState({});
  const [studentInstructions, setStudentInstructions] = useState("");
  const [assignmentPageClassList, setAssignmentPageClassList] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [articleData, setArticleData] = useState(null);
  const [assignmentPayloadMembers, setAssignmentPayloadMembers] = useState([]);
  // TODO -- LESS-1153: Could this be moved into the AssigneeSelectorModal component?
  const [assigneeSelectionStep, setAssigneeSelectionStep] = useState(
    ASSIGNEE_TYPE_SELECTION_STEP,
  );

  const isEditingExistingAssignment = assignment && assignment.content_id;
  const isCreatingNewAssignment = !isEditingExistingAssignment;

  // handle expiration date toast display for lite users
  const liteContentWithEarliestExpireDate =
    getLiteContentWithEarliestExpireDate(assignmentMembers);
  useExpirationWarning(liteContentWithEarliestExpireDate);

  // determine the maximum date that the assignment can be scheduled
  const maxScheduleDate = getExpirationDate(
    liteContentWithEarliestExpireDate,
    userProperties.isLiteUser,
  );

  // TODO -- LESS-1153: should the data returned from this hook be stored in state? We should store `teacherClassrooms` in state
  // in this component, then pass the setter to the custom hook `useClassroomGetList` -- then the custom hook should set state in this component
  const { data: teacherClassrooms } = useClassroomGetList();
  const getClassroomById = (id) => {
    return teacherClassrooms?.find((classroom) => {
      return id == classroom.id || classroom.unique_id == id;
    });
  };
  const hasExternalCourse = !!(
    externalAssignmentContext?.courseId && teacherClassrooms
  );
  let classroomChoices = teacherClassrooms;
  if (hasExternalCourse) {
    // Limit the classroom choices when external classroom is provided
    const classroom = getClassroomById(externalAssignmentContext?.courseId);
    // TODO -- LESS-1153: should classroomChoices be directly updated like this? It is
    // pointing to `teacherClassrooms` which is returned from a hook
    classroomChoices = classroom ? [classroom] : teacherClassrooms;
  }

  // Set the selected classroom when external classroom exists
  useEffect(() => {
    const hasNoAssignedClassrooms =
      assignment?.assignment_classrooms?.length === 0 &&
      currentSelectionOfClassrooms.length === 0;
    if (hasExternalCourse && hasNoAssignedClassrooms) {
      setCurrentSelectionOfClassrooms(classroomChoices);
    }
  }, [currentSelectionOfClassrooms, hasExternalCourse, classroomChoices]);

  const [postAssignment] = useAssignmentCreate();
  const [deleteAssignment] = useAssignmentDelete();

  // TODO -- LESS-1153: can any of these useModal's be moved into the children components that use them?
  const [
    classroomSelectorModalProps,
    classroomSelectorSetActive,
    classroomSelectorTriggerRef,
  ] = useModal(false);
  const [deleteAssignmentModalProps, deleteAssignmentSetActive] =
    useModal(false);
  const [
    createClassroomModalProps,
    createClassroomSetActive,
    createClassroomTriggerRef,
  ] = useModal(false);
  const [classroomInvitationModalProps, classroomInvitationSetActive] =
    useModal(false);
  const [
    discardChangesModalProps,
    discardChangesSetActive,
    discardChangesTriggerRef,
  ] = useModal(false);
  const [advisoryModalProps, setAdvisoryModalActive, advisoryTriggerRef] =
    useModal(false);
  const [
    assigneeSelectorModalProps,
    setAssigneeSelectorModalActive,
    assigneeSelectorModalTriggerRef,
  ] = useModal(false);

  const [globalLayoutHeaderHeight, setGlobalLayoutHeaderHeight] = useState("");
  const { undoHistory, addToUndoHistory, undo } = useUndo();

  const { width: windowWidth } = useWindowSize();

  const hasCompleteEngagement = useContext(UserCEContext);

  const [scheduledDateTime, setScheduledDateTime] = useState();

  const {
    data: newAssignmentMembers,
    loading: isLoadingNewAssignmentMembers,
    error,
  } = useContent(
    contentIds,
    { depth: 3, use_imgix: true },
    !contentIds?.length,
  );

  const isDueDateAssigned = dueDate?.length > 0;
  const onDueDateChange = (e) => {
    setDueDate(e.target.value);
  };

  // Only fetch content data for text-set if necessary:
  // TODO -- LESS-1153: do not conditionally call hook
  const textSet =
    contentIds?.length > 1
      ? useContent([createdFrom], { use_imgix: true }).data
      : null;
  let contentTitle;
  if (newAssignmentMembers) {
    const [firstMember] = newAssignmentMembers;
    // When assignment members load, set contentTitle, but check for textSet title first:
    contentTitle = textSet?.length ? textSet[0].title : firstMember?.title;
  }

  const onScheduledDateTimeChange = (e) => {
    setScheduledDateTime(e);
  };

  const setDisplayOrder = (members) => {
    return members.map((member, index) => {
      return {
        ...member,
        display_order: index,
      };
    });
  };

  useLayoutEffect(() => {
    const globalLayoutHeader =
      document.getElementsByTagName("header")[0]?.clientHeight;
    setGlobalLayoutHeaderHeight(globalLayoutHeader);
  }, [windowWidth]);

  useEffect(() => {
    // initialize current selections of classrooms/student assignees from existing:
    const initialClassroomSelections =
      assignment?.assignment_classrooms
        ?.map(({ classroom }) => classroom)
        .filter((classroom) => !isClassroomArchived(classroom)) || [];

    const [assignmentSmallGroup] = assignment?.assignment_groups || [];
    const initialStudentAssigneeSelections = assignmentSmallGroup?.users || [];
    const initialDueDate = assignment?.date_due
      ? convertUTCStringToLocalTime(assignment?.date_due)
      : null;

    // restructure assignment members if we're on the edit page based on API response
    const assignmentMembers =
      assignment?.assignment_members?.map((member) =>
        member.content ? member.content : member,
      ) || [];

    // if assignment title already set, use that. otherwise use title from selected content:
    const initialTitle = assignment?.title || contentTitle;

    const originalDateGoesLive = assignment?.date_goes_live;

    const assignmentIsDraft =
      assignment && !copiedAssignmentId ? assignment.is_draft : true; // copied assignments are a draft by default

    setAssignmentMembers(assignmentMembers);
    setCurrentSelectionOfClassrooms(initialClassroomSelections);
    setCurrentSelectionOfStudentAssignees(initialStudentAssigneeSelections);
    setTitle(initialTitle);
    setStudentInstructions(assignment?.instructions);
    setScheduledDateTime(originalDateGoesLive);
    setIsDraft(assignmentIsDraft);
    setDueDate(initialDueDate);
    setGradeLevel(
      assignment?.grade_level_lock ? assignment?.grade_level_lock : null,
    );
  }, [assignment, contentTitle]);

  // We access the content to assign from the url query params in order to create a new assignment
  useEffect(() => {
    // restructure assignment members if we're on the edit page based on API response
    let existingAssignmentMembers =
      assignment?.assignment_members?.map((member) =>
        member.content ? member.content : member,
      ) || [];

    if (newAssignmentMembers && !isLoadingNewAssignmentMembers) {
      const [flatAssignmentMembers, hitMaxLimit] = flattenAssignmentMembers(
        newAssignmentMembers,
        MAX_LIMIT_ASSIGNMENT_MEMBERS,
      );
      const setupAssignmentMembers = setDisplayOrder(flatAssignmentMembers);
      existingAssignmentMembers = [
        ...existingAssignmentMembers,
        ...setupAssignmentMembers,
      ];

      if (hitMaxLimit) {
        globalToasterRef.current.displayToast({
          statusColor: Toast.statusColor.warning,
          children: `This assignment was too large so it's been reduced to include only ${MAX_LIMIT_ASSIGNMENT_MEMBERS} pieces of content.`,
        });
      }
    }

    const geniusContentTypes =
      contentTypes.GENERATION_GENIUS_VIDEO.toLowerCase();

    const hasGeniusContent = existingAssignmentMembers.some(
      (member) => member?.content_type === geniusContentTypes,
    );

    if (hasGeniusContent) {
      globalToasterRef.current.displayToast({
        statusColor: Toast.statusColor.danger,
        children: `Generation Genius videos can only be assigned individually. To continue, please assign only one video at a time.`,
      });
    }

    const assignmentsWithoutGeniusVideos = existingAssignmentMembers.filter(
      (member) => {
        return member?.content_type !== geniusContentTypes;
      },
    );
    setAssignmentMembers(assignmentsWithoutGeniusVideos);
  }, [
    newAssignmentMembers,
    isLoadingNewAssignmentMembers,
    assignment?.assignment_members,
  ]);

  useEffect(() => {
    // filter content for articles
    setArticleData(
      assignmentMembers?.filter((member) =>
        ["articleheader", "articlestory"].includes(member?.content_type),
      ),
    );
    setAssignmentPayloadMembers(setDisplayOrder(assignmentMembers));
  }, [assignmentMembers]);

  const handleClassroomCreated = (newClassroom) => {
    // set the state of the newly created classroom to pass to ClassroomInvitation modal
    setNewClassroom(newClassroom);
    classroomChoices.push(newClassroom); // TODO -- LESS-1153: should classroomChoices be kept in state and updated via a setter? currently derived from hook
    fetchClassroomPayload();
  };

  const router = useRouter();

  const mustHaveTitle = () => {
    if (!title) {
      return { title: "An assignment must have a title." };
    }
  };
  const mustHaveContent = () => {
    if (!assignmentPayloadMembers.length) {
      return { content: "An assignment must have content." };
    }
  };
  const mustHaveAssignees = () => {
    if (
      !currentSelectionOfClassrooms?.length &&
      !currentSelectionOfStudentAssignees?.length
    ) {
      return {
        assignees:
          "An assignment must have at least one assignee, or you can save a draft.",
      };
    }
  };

  // TODO -- LESS-1153: can this be replaced with assignmentClassrooms.some(classroom => classroom.id === undefined) ?
  const verifyAssignmentClassroomStructure = (assignmentClassrooms) => {
    for (let pos = 0; pos < assignmentClassrooms.length; pos++) {
      if (assignmentClassrooms[pos].id === undefined) {
        return false;
      }
    }
    return true;
  };

  const catContent = (assignmentMembers || []).filter(
    (content) => content.veil?.reason === "DISTRICT_ADVISORY",
  );
  const srcContent = (assignmentMembers || []).filter((content = {}) =>
    getIsContentStudentRestricted(content?.content || content),
  );

  const containsSRCContent = getHasSRCContent(assignmentMembers);
  const consentedSRCContent = getConsentedSRCContent(assignmentMembers);

  const getContentIds = (advisoryContent) =>
    advisoryContent.reduce((acc, content) => {
      acc.push(content.content_id);
      return acc;
    }, []);
  const contentWithAdvisory = [...catContent, ...srcContent];
  const contentIdsWithSrcAdvisory = getContentIds(srcContent);
  const contentIdsWithCatAdvisory = getContentIds(catContent);
  const isCopiedAssignmentWithConsentedSRC = Boolean(
    copiedAssignmentId && consentedSRCContent.length,
  );

  const shouldDisplayContentAdvisory =
    Boolean(contentWithAdvisory.length) || isCopiedAssignmentWithConsentedSRC;

  const isEditPage = router?.route.slice(-4) === "edit";

  const determineSmallGroupName = (
    studentAssignees,
    n = MAX_STUDENT_ASSIGNEES_TO_LIST,
  ) => {
    return getTruncatedSmallGroupUserNames(studentAssignees, n);
  };

  const createAssignmentPostPayload = () => {
    // Unset either assignment_classrooms or assignment_groups, using [], when the other is present
    // (ensures either classrooms OR groups are assignees, but not both)
    let assignees = {}; // initialize to empty object in event that user is submitting draft, and has no assignees selected
    if (currentSelectionOfClassrooms.length) {
      assignees = {
        assignment_classrooms: [...currentSelectionOfClassrooms],
        assignment_groups: [],
      };
    } else if (currentSelectionOfStudentAssignees.length) {
      assignees = {
        assignment_groups: [
          {
            name: determineSmallGroupName(
              currentSelectionOfStudentAssignees,
              2,
            ),
            users: currentSelectionOfStudentAssignees.map((user) => ({
              unique_id: user.unique_id,
            })),
          },
        ],
        assignment_classrooms: [],
      };
    }

    return {
      ...(assignment?.content_id ? { content_id: assignment.content_id } : {}),
      assignment_classrooms: currentSelectionOfClassrooms,
      instructions: studentInstructions,
      grade_level_lock: gradeLevel,
      title,
      date_goes_live:
        scheduledDateTime || convertLocalTimeToUTCString(currentLocalDateTime),
      is_draft: isDraft,
      ...(isDueDateAssigned
        ? { date_due: convertLocalTimeToUTCString(dueDate) }
        : null),
      assignment_members: assignmentPayloadMembers,
      copied_from_assignment: copiedAssignmentId,
      ...assignees,
    };
  };

  /**
   * Creates an object of errors from the validation check functions.
   * If there are no errors, it executes the payload post to the API.
   * @param {array} validationChecks - Array of functions that return error objects.
   */
  const validateAssignment = (
    validationChecks,
    isDraft = false,
    finalizeDraft = false,
  ) => {
    let assignmentErrors = {};

    validationChecks.forEach((check) => {
      const hasError = check();
      if (hasError) {
        assignmentErrors = Object.assign(assignmentErrors, hasError);
      }
    });

    if (Object.keys(assignmentErrors).length > 0) {
      setValidationErrors(assignmentErrors);

      if (Object.keys(assignmentErrors).length > 0) {
        globalToasterRef.current.displayToast({
          statusColor: Toast.statusColor.danger,
          children: validationToastMessage(assignmentErrors),
        });
      }

      return;
    }

    if (!isDraft && !finalizeDraft && shouldDisplayContentAdvisory) {
      setAdvisoryModalActive(true);
      return;
    }

    const assignmentPostPayload = createAssignmentPostPayload();

    if (isDraft && !finalizeDraft) {
      postDraftAssignment(assignmentPostPayload);
    } else {
      postLiveAssignment(assignmentPostPayload);
    }
  };

  const postAdvisoryAssignment = () => {
    const assignmentPostPayload = createAssignmentPostPayload();
    // TODO -- LESS-1153: it seems that regular assignment post flow runs through validation in validateAssignment first -- however, this directly calls postLiveAssignment
    // Are advisory assignments passing through required check?
    postLiveAssignment(assignmentPostPayload);
  };

  const renderLevelLockMenu = () => {
    useEffect(() => {
      if (!error) return;
      globalToasterRef.current.displayToast({
        statusColor: Toast.statusColor.danger,
        children: "Something went wrong. Try reloading this page.",
      });
    }, [error]);

    if (error) {
      return (
        <div css={levelLockingLoadingWrapperCss}>
          <LoadingShimmer />
        </div>
      );
    }
    if (isLoadingNewAssignmentMembers) {
      return (
        <div css={levelLockingLoadingWrapperCss}>
          <LoadingShimmer />
        </div>
      );
    }
    let hasLevels = false;

    assignmentMembers?.forEach((member) => {
      if (member?.grade_levels) {
        hasLevels = true;
      }
    });

    const renderLevelLockHelperText = (
      <p css={levelLockHelperTextCSS}>
        Students <span>{gradeLevel === null ? "can" : "can't"}</span> change
        reading levels
      </p>
    );

    const renderFormattedLevels = (level) => {
      const [, range] = gradeLevelFormatter(+level.split(" ")[1]);
      return (
        <div>
          <span>{level}</span>
          <span>{range}</span>
        </div>
      );
    };

    // The loading state of the assignment is done by the Edit Page.
    if (hasLevels) {
      return (
        <LevelLockMenu
          __css={levelLockMenuCSS}
          helperText={renderLevelLockHelperText}
          label="Text level locking"
          levels={getLexileLevelArray(articleData)}
          selectedItem={(e) => {
            if (e?.selectedItem !== "No level locking") {
              setGradeLevel(+e.selectedItem?.split(" ")[1]);
            } else {
              setGradeLevel(null);
            }
          }}
          defaultValue={
            gradeLevel ? gradeLevelFormatter(gradeLevel)[0] : "No level locking"
          }
          renderItem={(grade) => renderFormattedLevels(grade)}
        />
      );
    }

    return (
      <div>
        <Input
          helperText="Level locking is for article content only"
          name="level-lock-input"
          label="Text level locking"
          value="No levels available"
          disabled
          __cssFor={{
            root: levelLockMenuCSS,
          }}
        />
      </div>
    );
  };

  // TODO -- LESS-1153: is this a component?
  const renderExternalToolBanner = () => {
    const selectedClass = getClassroomById(
      parseInt(externalAssignmentContext?.courseId),
    );
    const courseTitle =
      selectedClass?.name || externalAssignmentContext?.courseTitle;
    const message = (
      <>
        You're creating an assignment for your&nbsp;
        <strong>"{courseTitle}"</strong>&nbsp;
        {capitalize(externalAssignmentContext?.provider)} class
      </>
    );

    return <Banner>{message}</Banner>;
  };

  // TODO -- LESS-1153: lets move hooks to all be called from the same place at the top of the component (besides useEffects)
  const { data: createdFromContent } = useContent(createdFrom, {
    use_imgix: true,
  });

  const postDraftAssignment = async (payload) => {
    try {
      if (createdFromContent) {
        // If the assignment was originally created from a bundle (i.e. textset), then add that to the assignment payload
        if (
          createdFromContent.content_type !== "video" ||
          createdFromContent.content_type !== "audio" ||
          createdFromContent.content_type !== "articleheader"
        ) {
          payload.source_content_id = createdFrom;
        }
      }
      // TODO -- LESS-1153: the following key's value is already set to true in validateAssignment on line 411 (is the conditional checked before this fn is called)
      payload.is_draft = true;
      if (!verifyAssignmentClassroomStructure(payload.assignment_classrooms)) {
        return;
      }
      setIsSaving(true);
      const res = await postAssignment(payload);
      redirectUser(res);
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const postLiveAssignment = async (payload) => {
    try {
      // Don't schedule assignments past the maxScheduleDate
      const isScheduledPastExpireDate =
        Boolean(maxScheduleDate) && scheduledDateTime > maxScheduleDate;
      if (isScheduledPastExpireDate) {
        globalToasterRef.current.displayToast({
          statusColor: Toast.statusColor.danger,
          children: `Please schedule the assignment before the article's expiration on ${getLocalISOStringDate(maxScheduleDate, "MM/DD/YYYY")}.`,
        });
        return;
      }

      const dateNow = new Date();
      const addDateAssigned = (assigneeGrouping) => {
        const groups = assignment?.assignment_groups;
        let dateAssigned = dateNow;
        if (groups?.length && groups[0]?.date_assigned) {
          dateAssigned = groups[0].date_assigned;
        }
        assigneeGrouping.date_assigned = dateAssigned;
      };
      // Add assign date to each assignee grouping. In the future these dates can
      // be unique per classroom.
      payload.assignment_classrooms.forEach(addDateAssigned);
      payload.assignment_groups.forEach(addDateAssigned);

      if (shouldDisplayContentAdvisory) {
        if (catContent.length) {
          payload.district_advisory_override = contentIdsWithCatAdvisory.join();
        }
        if (srcContent.length) {
          payload.content_settings_override = contentIdsWithSrcAdvisory.join();
        }

        // ensure content_settings_override key is passed for copied assignment w/ SRC
        if (isCopiedAssignmentWithConsentedSRC) {
          // pass consented content ids to avoid 400 error
          payload.content_settings_override = consentedSRCContent
            .map((content) => content.content_id)
            .filter((contentId) => contentId)
            .join();

          // remove overrides to trigger consent modal on initial save of copied assignment
          payload.assignment_members?.forEach((assignmentMember) => {
            if (assignmentMember?.user_content_settings_override) {
              delete assignmentMember.user_content_settings_override;
            }
          });
        }
      }

      // TODO -- LESS-1153: the following key's value is already set to false in validateAssignment on line 413 (is the conditional checked before this fn is called)
      payload.is_draft = false;
      if (!verifyAssignmentClassroomStructure(payload.assignment_classrooms)) {
        return;
      }
      setIsSaving(true);
      const res = await postAssignment(payload);
      redirectUser(res);
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const handleDelete = () => {
    if (isDraft) {
      setDeleteAssignmentMsg(
        "Are you sure you want to delete this assignment?",
      );
    } else {
      setDeleteAssignmentMsg(
        "Deleting this assignment will unassign it from your classes. Are you sure you want to delete this assignment?",
      );
    }

    deleteAssignmentSetActive(true);
  };

  // TODO -- LESS-1153: should the following assignmentCreateOrUpdateText and assignmentEventProperties be functions that return these values?
  const assignmentCreateOrUpdateText = isEditingExistingAssignment
    ? "Update"
    : "Create";

  const actionName = isEditingExistingAssignment
    ? actionNames.update
    : actionNames.create;

  const assignmentEventProperties = {
    content_id: assignment?.content_id || null,
    content_type: "Assignment",
    content_ids: assignmentPayloadMembers?.map((member) => member.content_id),
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setAssignmentMembers((items) => {
        const originalIndex = items.findIndex(
          (item) => item.content_id === active.id,
        );
        const newIndex = items.findIndex((item) => item.content_id === over.id);
        return arrayMove(items, originalIndex, newIndex);
      });
    }
  };

  const handleDeleteCard = (e, id) => {
    const clickedCard = assignmentMembers.find(
      (member) => member.content_id === id,
    );
    addToUndoHistory(assignmentMembers);
    setAssignmentMembers(
      assignmentMembers.filter(
        (member) => member.content_id !== clickedCard.content_id,
      ),
    );
  };

  const handleMoveStep = (direction, id) => {
    setAssignmentMembers((items) => {
      const originalIndex = items.findIndex((item) => item.content_id === id);
      const newIndex =
        originalIndex + (direction === StepItem.moveDirections.up ? -1 : 1);
      return arrayMove(items, originalIndex, newIndex);
    });
  };

  const validationToastMessage = (errorObject) => {
    const errors = Object.keys(errorObject);
    const errorCopyMap = {
      assignees: "an assignee",
      content: "content",
      title: "a title",
      dueDate: "a future due date and time",
    };
    let errorText = "Please add ";

    errors.forEach((error, i) => {
      if (errors.length === 1) {
        errorText = errorText.concat(`${errorCopyMap[error]}`);
      }
      if (errors.length === 2) {
        if (i === 0) errorText = errorText.concat(`${errorCopyMap[error]}`);
        if (i === 1) errorText = errorText.concat(` & ${errorCopyMap[error]}`);
      }
      if (errors.length === 3) {
        if (i === 0) errorText = errorText.concat(`${errorCopyMap[error]}`);
        if (i === 0) errorText = errorText.concat(`, ${errorCopyMap[error]}`);
        if (i === 1) {
          errorText = errorText.concat(`, & ${errorCopyMap[error]}`);
        }
      }
    });

    errorText = errorText.concat(" to create this assignment");
    return errorText;
  };

  const handleNewClassFocus = (ref) => {
    classroomInvitationSetActive(false);
    // TODO -- LESS-1153: Can this be achieved with useEffect? Would be nice to look into removing this React gotcha if React has relevant updates since this was written
    // a timeout is necessary here to get at deeply nested elements https://stackoverflow.com/questions/35522220/react-ref-with-focus-doesnt-work-without-settimeout-my-example
    setTimeout(() => {
      ref?.current && ref.current.focus();
    });
  };

  const handleRedirectOnCancel = () => {
    const queryReturnURL = router.query.return_url;

    const formattedURL = queryReturnURL ? urlFormatter(queryReturnURL) : null;

    // absolute urls should be pushed directly to router
    // in order to properly redirect back to LyceumOrigin
    if (formattedURL && formattedURL.startsWith("http")) {
      if (isAllowedUrl(formattedURL)) {
        return router.push(`${formattedURL}`);
      } else {
        return router.push(`${MONOLITH_ORIGIN}`);
      }
    }
    if (formattedURL) {
      return router.push(`${MONOLITH_ORIGIN}/${formattedURL}`);
    }

    if (assignment?.content_id && !isDraft) {
      return router.push(
        `${MONOLITH_ORIGIN}/assignment/${assignment.content_id}`,
      );
    } else {
      return router.push(`${MONOLITH_ORIGIN}/binder/assignments`);
    }
  };

  const cancelSaveOptions = () => {
    let saveOptionValue;
    if (isDraft) {
      saveOptionValue = (
        <ButtonWithTrackEvent
          {...sharedNavTracking}
          streamProperties={{
            ...assignmentEventProperties,
            description: `${assignmentCreateOrUpdateText} assignment draft.`,
          }}
          flavor={Button.flavors.polarTertiary}
          onClick={() => {
            validateAssignment([mustHaveTitle, mustHaveContent], true);
            discardChangesSetActive(false);
          }}
        >
          Save changes
        </ButtonWithTrackEvent>
      );
    } else {
      saveOptionValue = (
        <ButtonWithTrackEvent
          {...sharedNavTracking}
          flavor={
            isEditPage && isDraft
              ? Button.flavors.secondary
              : Button.flavors.primary
          }
          onClick={() => {
            validateAssignment([
              mustHaveTitle,
              mustHaveContent,
              mustHaveAssignees,
            ]);
          }}
        >
          Save changes
        </ButtonWithTrackEvent>
      );
    }
    return saveOptionValue;
  };

  const sharedNavTracking = {
    objectType: objectTypes.assignment,
    actionName,
    streamProperties: {
      ...assignmentEventProperties,
      description: `${assignmentCreateOrUpdateText} assignment and assign to classrooms.`,
    },
  };

  const sharedButtonProps = {
    size: Button.sizes.md,
  };

  const saveAndEditCheck = () => {
    if (isEditPage) {
      // TODO -- LESS-1153: following are very similar, could likely simplify by placing conditional at onClick fn
      return isDraft ? (
        <ButtonWithTrackEvent
          {...sharedNavTracking}
          {...sharedButtonProps}
          flavor={Button.flavors.secondary}
          onClick={() => {
            return validateAssignment([mustHaveTitle, mustHaveContent], true);
          }}
          disabled={isSaving}
        >
          Save changes
        </ButtonWithTrackEvent>
      ) : (
        <ButtonWithTrackEvent
          {...sharedNavTracking}
          {...sharedButtonProps}
          flavor={Button.flavors.primary}
          onClick={() =>
            validateAssignment([
              mustHaveTitle,
              mustHaveContent,
              mustHaveAssignees,
            ])
          }
          disabled={isSaving}
        >
          Save changes
        </ButtonWithTrackEvent>
      );
    }

    const saveBtnQASelector = contentWithAdvisory
      ? "cat_editor_save_assignment_button"
      : "editor_save_assignment_button";
    return (
      <ButtonWithTrackEvent
        {...sharedNavTracking}
        {...sharedButtonProps}
        streamProperties={{
          ...assignmentEventProperties,
          description: `${assignmentCreateOrUpdateText} assignment draft.`,
        }}
        flavor={Button.flavors.secondary}
        onClick={() => {
          validateAssignment([mustHaveTitle, mustHaveContent], true);
        }}
        disabled={isSaving}
        dataProps={{
          "data-qa-selector": saveBtnQASelector,
        }}
      >
        Save draft
      </ButtonWithTrackEvent>
    );
  };

  // TODO -- LESS-1153: This pattern (determining a value based on a condition by using a function) seems safe and defends against stale values
  // being used. Let's update other areas to use this pattern (line 592 TODO)
  const renderPageTitle = () =>
    isEditPage ? "Edit assignment" : "Create assignment";

  // TODO -- LESS-1153: should this fn be called each time classroom selections updates? or only when the page loads with initial classroom selections?
  const fetchClassroomPayload = async () => {
    try {
      const assignmentClassroomIds =
        currentSelectionOfClassrooms?.map((ac) => ac.id) || [];
      const classroomApiResponses = await Promise.all(
        assignmentClassroomIds.map((assignmentClassroomId) =>
          fetchJson(
            `${API_BASE_URL}/api/v2/classroom/${assignmentClassroomId}/`,
          ),
        ),
      );
      setAssignmentPageClassList(classroomApiResponses);
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const sharedToastProp = {
    alignment: Toast.alignment.center,
    statusColor: Toast.statusColor.primary,
  };

  useEffect(() => {
    fetchClassroomPayload();
  }, [currentSelectionOfClassrooms]);

  const renderNoStudentsToastMessage = () => {
    const classroomsWithNoStudents = assignmentPageClassList.filter(
      (selectedClassroom) => selectedClassroom?.students?.length < 1,
    );
    if (classroomsWithNoStudents.length === 1) {
      return classroomsWithNoStudents.map((classroom) => (
        <Toast
          key={classroom.id}
          {...sharedToastProp}
          cta={{
            onClick: () => {
              classroomInvitationSetActive(true);
              setNewClassroom(classroom);
            },
            text: "Invite Students",
          }}
          __cssFor={{
            cta: toastMessageCta,
            root: toastMessageSize,
          }}
        >
          <p>
            {" "}
            Your class, <strong>{classroom.name}</strong>, doesn't have any
            students yet
          </p>
        </Toast>
      ));
    } else if (classroomsWithNoStudents.length > 1) {
      return (
        <Toast
          {...sharedToastProp}
          alignment={Toast.alignment.left}
          __cssFor={{
            root: toastMessageSize,
          }}
        >
          <p>
            {" "}
            <strong>
              {classroomsWithNoStudents
                .map((classroom) => classroom.name)
                .join(", ")
                .replace(/, ((?:.(?!, ))+)$/, ", and $1")}
            </strong>{" "}
            doesn’t have any students. Add students to your classes on the{" "}
            <strong>Class Settings</strong> page
          </p>
        </Toast>
      );
    }
    return <div css={toastMessageSpacing} />;
  };

  // TODO -- LESS-1153: confusing variable name here:
  const canHasRenderExternalToolBanner =
    externalAssignmentContext?.courseId && classroomChoices?.length;

  const goToBinderAssignments = () => {
    if (canHasRenderExternalToolBanner) {
      router.push(
        `${MONOLITH_ORIGIN}/binder/assignments?showAssignmentExistPopup=true`,
      );
    } else {
      router.push(`${MONOLITH_ORIGIN}/binder/assignments`);
    }
  };

  const goToAssignment = (assignmentContentId) => {
    router.push(`${MONOLITH_ORIGIN}/assignment/${assignmentContentId}`);
  };

  const redirectUser = ({ data }) => {
    if (data?.content_id) {
      goToAssignment(data.content_id);
    } else {
      goToBinderAssignments();
    }
  };

  const MemorizedSortableDeckOfCards = useMemo(() => {
    return (
      <>
        {!hasCompleteEngagement && (
          <ol css={gridLayoutCss}>
            <SortableDeckOfCards
              css={cardCss}
              onDragEnd={handleDragEnd}
              items={assignmentMembers}
              handleDeleteCard={(e, id) => handleDeleteCard(e, id)}
            />
          </ol>
        )}
      </>
    );
  }, [assignmentMembers, hasCompleteEngagement]);

  const MemorizedListOfSteps = useMemo(() => {
    return (
      <>
        {!!hasCompleteEngagement && (
          <ol css={stepLayoutCss}>
            <ListOfSteps
              assignmentMembers={assignmentMembers}
              handleDeleteStep={(e, id) => handleDeleteCard(e, id)}
              handleMoveStep={handleMoveStep}
            />
          </ol>
        )}
      </>
    );
  }, [assignmentMembers, hasCompleteEngagement]);
  // TODO -- LESS-1153: Could this setAssigneeSelectionStep logic be moved into the AssigneeSelectorModal component?
  const renderAssigneeModal = () => {
    setAssigneeSelectorModalActive(true);
    if (isEditPage && currentSelectionOfClassrooms.length > 0) {
      setAssigneeSelectionStep(CLASSROOMS_SELECTION_STEP);
    }

    if (isEditPage && currentSelectionOfStudentAssignees.length > 0) {
      setAssigneeSelectionStep(STUDENTS_SELECTION_STEP);
    }
  };

  return (
    <>
      <nav css={navigationCSS} aria-label="navigation">
        <h1 css={pageTitleCSS}>{renderPageTitle()}</h1>
        <div css={navButtonRowCSS}>
          <ButtonWithTrackEvent
            {...sharedButtonProps}
            objectType={objectTypes.assignment}
            actionPrefix={actionPrefixes.cancel}
            actionName={actionName}
            streamProperties={{
              ...assignmentEventProperties,
              description: "Cancel editing assignment.",
            }}
            flavor={Button.flavors.polarTertiary}
            onClick={() => {
              discardChangesSetActive(true);
            }}
            ref={discardChangesTriggerRef}
          >
            Cancel
          </ButtonWithTrackEvent>
          <InfoModal
            __cssFor={{
              modalContentWrapper: discardChangesModalCss,
              root: { zIndex: "10" }, // any value 10 or greater, to surpass global header's z-index
            }}
            labelId="DiscardChanges"
            title="Discard Changes?"
            {...discardChangesModalProps}
            footer={
              <div css={buttonRowCSS}>
                {cancelSaveOptions()}
                <ButtonWithTrackEvent
                  objectType={objectTypes.assignment}
                  actionPrefix={actionPrefixes.cancel}
                  actionName={actionName}
                  streamProperties={{
                    ...assignmentEventProperties,
                    description: "Cancel editing assignment.",
                  }}
                  flavor={Button.flavors.danger}
                  onClick={() => {
                    discardChangesSetActive(false);
                    handleRedirectOnCancel();
                  }}
                >
                  Yes, Discard Changes
                </ButtonWithTrackEvent>
              </div>
            }
          >
            <div>
              <p>You have unsaved work. Are you sure you want to discard? </p>
            </div>
          </InfoModal>
          {
            // only show "Delete" for existing assignments
            assignment?.content_id && !copiedAssignmentId ? (
              // TODO -- LESS-1153: Interested in understanding how the following works without a ref being passed to this button, as is
              // done elsewhere with buttons that trigger a modal open (discardChangesTriggerRef, classroomSelectorTriggerRefm, createClassroomTriggerRef, etc)
              <ButtonWithTrackEvent
                {...sharedButtonProps}
                objectType={objectTypes.assignment}
                actionPrefix={actionPrefixes.initiate}
                actionName={actionNames.delete}
                streamProperties={{
                  ...assignmentEventProperties,
                  description: "Open modal to delete assignment",
                }}
                flavor={Button.flavors.danger}
                onClick={() => handleDelete()}
              >
                Delete
              </ButtonWithTrackEvent>
            ) : null
          }
          {saveAndEditCheck()}
          <AdvisoryModal
            advisoryModalProps={advisoryModalProps}
            setAdvisoryModalActive={setAdvisoryModalActive}
            assignmentMembers={newAssignmentMembers}
            handleAdvisoryConfirmation={() => postAdvisoryAssignment()}
            isStudentRestrictedContent={getHasSRCContent(
              assignmentMembers || [],
            )}
          />
          {isDraft ? (
            <ButtonWithTrackEvent
              {...sharedNavTracking}
              {...sharedButtonProps}
              flavor={Button.flavors.primary}
              ref={advisoryTriggerRef}
              onClick={() =>
                validateAssignment(
                  [mustHaveTitle, mustHaveContent, mustHaveAssignees],
                  false,
                  false,
                )
              }
            >
              Assign
            </ButtonWithTrackEvent>
          ) : null}
        </div>
      </nav>
      {renderNoStudentsToastMessage()}
      <div css={assignmentEditorLayoutCss}>
        {canHasRenderExternalToolBanner && renderExternalToolBanner()}
        <section css={assignmentSectionCss}>
          {/* Show live badge only when the user is on the edit page */}
          {isEditPage && (
            <div css={{ padding: "1rem 0rem" }}>
              {isDraft ? null : (
                <Badge
                  text={
                    isFutureDateTime(assignment?.date_goes_live)
                      ? "scheduled"
                      : "live"
                  }
                  flavor={Badge.flavor.solid}
                />
              )}
            </div>
          )}
          <div css={baseDetailsRowCss}>
            <Input
              name="assignmentTitle"
              label="Assignment title"
              onChange={(e) => {
                setTitle(e.target.value);
              }}
              value={title}
              error={titleTouched && !title && validationErrors.title}
              onBlur={() => {
                if (!titleTouched) {
                  setTitleTouched(true);
                }
                setValidationErrors({
                  title: "An assignment must have a title.",
                });
              }}
              touched={titleTouched}
              __cssFor={{
                root: assignmentTitleInputCSS,
              }}
            />
            {renderLevelLockMenu()}
          </div>
          <div css={inputsRowCss}>
            <ScheduledDateTimeInput
              isEditingExistingAssignment={isEditingExistingAssignment}
              scheduledDateTime={scheduledDateTime}
              onScheduledDateTimeChange={onScheduledDateTimeChange}
              max={maxScheduleDate}
              originalDateGoesLive={assignment?.date_goes_live}
            />
          </div>

          {/* Assignee Selector will contain assignee list, assignee modal button trigger, create new class button trigger */}
          <AssigneeSelector
            hasAssignees={Boolean(
              currentSelectionOfClassrooms?.length ||
                currentSelectionOfStudentAssignees?.length,
            )}
            classroomSelectorModalProps={classroomSelectorModalProps}
            classroomSelectorSetActive={classroomSelectorSetActive}
            classroomSelectorTriggerRef={classroomSelectorTriggerRef}
            createClassroomSetActive={createClassroomSetActive}
            createClassroomTriggerRef={createClassroomTriggerRef}
            determineSmallGroupName={determineSmallGroupName}
            currentSelectionOfClassrooms={currentSelectionOfClassrooms}
            setCurrentSelectionOfClassrooms={setCurrentSelectionOfClassrooms}
            currentSelectionOfStudentAssignees={
              currentSelectionOfStudentAssignees
            }
            setCurrentSelectionOfStudentAssignees={
              setCurrentSelectionOfStudentAssignees
            }
            renderAssigneeModal={renderAssigneeModal}
            showWarningIcon={!!validationErrors.assignees}
            assigneeSelectorModalTriggerRef={assigneeSelectorModalTriggerRef}
            showCreateClass={canCreateClassrooms}
          />

          <AssigneeSelectorModal
            assigneeSelectorModalProps={assigneeSelectorModalProps}
            assignmentClassrooms={
              assignment?.assignment_classrooms?.map(
                ({ classroom }) => classroom,
              ) || []
            }
            assignmentStudentAssignees={
              assignment?.assignment_groups?.["0"]?.users || []
            }
            classroomChoices={classroomChoices}
            currentSelectionOfClassrooms={currentSelectionOfClassrooms}
            currentSelectionOfStudentAssignees={
              currentSelectionOfStudentAssignees
            }
            isEditPage={isEditPage}
            setAssigneeSelectorModalActive={setAssigneeSelectorModalActive}
            setCurrentSelectionOfClassrooms={setCurrentSelectionOfClassrooms}
            setCurrentSelectionOfStudentAssignees={
              setCurrentSelectionOfStudentAssignees
            }
            setAssigneeSelectionStep={setAssigneeSelectionStep}
            assigneeSelectionStep={assigneeSelectionStep}
          />
          {canCreateClassrooms && (
            <CreateNewClassroomModal
              createClassroomModalProps={createClassroomModalProps}
              handleCancel={() => {
                createClassroomSetActive(false);
              }}
              submitCallback={(postResponse) => {
                createClassroomSetActive(false);
                handleClassroomCreated(postResponse);
                classroomInvitationSetActive(true);
              }}
              __cssFor={{
                modalContentWrapper: modalContentWrapperCss(
                  globalLayoutHeaderHeight,
                ),
              }}
            />
          )}
          <ClassroomInvitationModal
            classroomInvitationModalProps={classroomInvitationModalProps}
            classroom={newClassroom}
            closeButtonProps={{
              onClick: () => handleNewClassFocus(createClassroomTriggerRef),
            }}
          />
          <InfoModal
            labelId="DeleteAssignment"
            title="Delete Assignment?"
            {...deleteAssignmentModalProps}
            __cssFor={{ root: { zIndex: "10" } }} // any value 10 or greater, to surpass global header's z-index
            footer={
              <div css={buttonRowCSS}>
                <Button
                  flavor={Button.flavors.polarSecondary}
                  onClick={() => {
                    deleteAssignmentSetActive(false);
                  }}
                >
                  Cancel
                </Button>
                <ButtonWithTrackEvent
                  objectType={objectTypes.assignment}
                  actionName={actionNames.delete}
                  streamProperties={{
                    ...assignmentEventProperties,
                    description: "Delete assignment",
                  }}
                  flavor={Button.flavors.danger}
                  onClick={() => {
                    deleteAssignment(assignment?.content_id);
                    deleteAssignmentSetActive(false);
                    goToBinderAssignments();
                  }}
                >
                  Delete
                </ButtonWithTrackEvent>
              </div>
            }
          >
            {deleteAssignmentMsg}
          </InfoModal>
        </section>

        <section css={assignmentContainerCss}>
          <div css={assignmentStudentInstructionsCss}>
            <div>
              <section css={assignmentSectionCss}>
                <div>
                  <div css={assignmentEditorLabelCss}>Student instructions</div>
                  {/**
                   * For copied assignments, instructions will initially be undefined,
                   * and Tiptap returns <p></p> if no instructions are entered. So render a
                   * loading spinner until the instructions are loaded and truthy. For all other
                   * assignments, i.e., those without the copiedAssignmentId query param, render
                   * the text editor.
                   */}
                  {(assignment?.instructions && copiedAssignmentId) ||
                  !copiedAssignmentId ? (
                    /* The ReadAloudContextProvider is required to re-use the extensions that are used in the TiptapArticleBody */
                    <ReadAloudContextProvider>
                      <RichTextEditor
                        content={assignment?.instructions ?? ""}
                        onChange={(instructionsMarkup) => {
                          setStudentInstructions(instructionsMarkup);
                        }}
                      />
                    </ReadAloudContextProvider>
                  ) : (
                    <LoadingSpinner />
                  )}
                </div>
              </section>
            </div>
          </div>

          {hasCompleteEngagement && (
            <>
              <h2 css={assignmentTitleCss}>Assignment steps</h2>
              <div css={[assignmentSectionCss, assignmentSubTitleCss]}>
                Create a new step here by selecting "Add to assignment" in
                another Newsela article or video.
              </div>
            </>
          )}
          {undoHistory.length > 0 && (
            <ButtonWithTrackEvent
              objectType={objectTypes.assignment}
              actionPrefix={actionPrefixes.undo}
              actionName={actionNames.update}
              streamProperties={{
                ...assignmentEventProperties,
                description: "Undo removal of assignment member.",
              }}
              flavor={Button.flavors.secondary}
              onClick={() => setAssignmentMembers(undo())}
            >
              Undo Removal
            </ButtonWithTrackEvent>
          )}
          <div css={assignmentSectionCss}>
            <p css={contentLabelCss}>Content</p>
            <p css={disclaimerCss}>
              Note: Assignments with multimedia content can't be viewed on the
              Newsela mobile app at this time.
            </p>
            {MemorizedSortableDeckOfCards}
            {MemorizedListOfSteps}
          </div>
        </section>
      </div>
    </>
  );
};

AssignmentEditor.propTypes = propTypes;

export default withTrackEvent(AssignmentEditor);
